/**
 * Audio manager with procedural sound generation
 * No external audio files needed - generates retro synth sounds
 */
export class AudioManager {
  private ctx: AudioContext | null = null;
  private masterGain: GainNode | null = null;
  private enabled: boolean = true;
  private initialized: boolean = false;

  /**
   * Initialize audio context (must be called after user interaction)
   */
  init(): void {
    if (this.initialized) return;

    try {
      this.ctx = new AudioContext();
      this.masterGain = this.ctx.createGain();
      this.masterGain.connect(this.ctx.destination);
      this.masterGain.gain.value = 0.3;
      this.initialized = true;
    } catch (e) {
      console.warn('Web Audio not supported');
      this.enabled = false;
    }
  }

  /**
   * Resume audio context if suspended
   */
  resume(): void {
    if (this.ctx?.state === 'suspended') {
      this.ctx.resume();
    }
  }

  /**
   * Set master volume (0-1)
   */
  setVolume(volume: number): void {
    if (this.masterGain) {
      this.masterGain.gain.value = Math.max(0, Math.min(1, volume));
    }
  }

  /**
   * Toggle audio on/off
   */
  toggle(): void {
    this.enabled = !this.enabled;
  }

  /**
   * Play laser shoot sound
   */
  playLaser(): void {
    if (!this.canPlay()) return;

    const ctx = this.ctx!;
    const now = ctx.currentTime;

    // Create oscillator for laser "pew"
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(this.masterGain!);

    osc.type = 'square';
    osc.frequency.setValueAtTime(880, now);
    osc.frequency.exponentialRampToValueAtTime(220, now + 0.1);

    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);

    osc.start(now);
    osc.stop(now + 0.1);
  }

  /**
   * Play explosion sound
   */
  playExplosion(size: number = 1): void {
    if (!this.canPlay()) return;

    const ctx = this.ctx!;
    const now = ctx.currentTime;
    const duration = 0.3 * size;

    // Noise burst for explosion
    const bufferSize = ctx.sampleRate * duration;
    const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
    const data = buffer.getChannelData(0);

    // Generate noise
    for (let i = 0; i < bufferSize; i++) {
      data[i] = (Math.random() * 2 - 1) * Math.exp(-i / (bufferSize * 0.3));
    }

    const noise = ctx.createBufferSource();
    noise.buffer = buffer;

    // Low-pass filter for "boom" quality
    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.setValueAtTime(1000 * size, now);
    filter.frequency.exponentialRampToValueAtTime(100, now + duration);

    const gain = ctx.createGain();
    gain.gain.setValueAtTime(0.3 * size, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    noise.connect(filter);
    filter.connect(gain);
    gain.connect(this.masterGain!);

    noise.start(now);
    noise.stop(now + duration);

    // Add bass thump
    const osc = ctx.createOscillator();
    const oscGain = ctx.createGain();

    osc.type = 'sine';
    osc.frequency.setValueAtTime(80 * size, now);
    osc.frequency.exponentialRampToValueAtTime(30, now + duration);

    oscGain.gain.setValueAtTime(0.4 * size, now);
    oscGain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    osc.connect(oscGain);
    oscGain.connect(this.masterGain!);

    osc.start(now);
    osc.stop(now + duration);
  }

  /**
   * Play pickup sound
   */
  playPickup(): void {
    if (!this.canPlay()) return;

    const ctx = this.ctx!;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(this.masterGain!);

    osc.type = 'sine';
    osc.frequency.setValueAtTime(440, now);
    osc.frequency.exponentialRampToValueAtTime(880, now + 0.1);

    gain.gain.setValueAtTime(0.15, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    osc.start(now);
    osc.stop(now + 0.15);
  }

  /**
   * Play turn-in success sound
   */
  playTurnIn(crateCount: number): void {
    if (!this.canPlay()) return;

    const ctx = this.ctx!;
    const now = ctx.currentTime;

    // Ascending arpeggio
    const notes = [523, 659, 784, 1047]; // C5, E5, G5, C6
    const numNotes = Math.min(crateCount, notes.length);

    for (let i = 0; i < numNotes; i++) {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.connect(gain);
      gain.connect(this.masterGain!);

      osc.type = 'triangle';
      osc.frequency.value = notes[i];

      const noteStart = now + i * 0.08;
      gain.gain.setValueAtTime(0, noteStart);
      gain.gain.linearRampToValueAtTime(0.2, noteStart + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.01, noteStart + 0.2);

      osc.start(noteStart);
      osc.stop(noteStart + 0.25);
    }
  }

  /**
   * Play hit sound (player hit)
   */
  playHit(): void {
    if (!this.canPlay()) return;

    const ctx = this.ctx!;
    const now = ctx.currentTime;

    // Distorted hit sound
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const distortion = ctx.createWaveShaper();

    // Create distortion curve
    const curve = new Float32Array(256);
    for (let i = 0; i < 256; i++) {
      const x = (i / 128) - 1;
      curve[i] = Math.tanh(x * 3);
    }
    distortion.curve = curve;

    osc.connect(distortion);
    distortion.connect(gain);
    gain.connect(this.masterGain!);

    osc.type = 'sawtooth';
    osc.frequency.setValueAtTime(200, now);
    osc.frequency.exponentialRampToValueAtTime(50, now + 0.15);

    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.15);

    osc.start(now);
    osc.stop(now + 0.15);
  }

  /**
   * Play thrust/engine sound (continuous, call each frame when thrusting)
   */
  private thrustOsc: OscillatorNode | null = null;
  private thrustGain: GainNode | null = null;

  startThrust(): void {
    if (!this.canPlay() || this.thrustOsc) return;

    const ctx = this.ctx!;

    this.thrustOsc = ctx.createOscillator();
    this.thrustGain = ctx.createGain();

    const filter = ctx.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 200;

    this.thrustOsc.connect(filter);
    filter.connect(this.thrustGain);
    this.thrustGain.connect(this.masterGain!);

    this.thrustOsc.type = 'sawtooth';
    this.thrustOsc.frequency.value = 55;
    this.thrustGain.gain.value = 0.08;

    this.thrustOsc.start();
  }

  stopThrust(): void {
    if (this.thrustOsc) {
      this.thrustOsc.stop();
      this.thrustOsc = null;
      this.thrustGain = null;
    }
  }

  /**
   * Play UI click sound
   */
  playClick(): void {
    if (!this.canPlay()) return;

    const ctx = this.ctx!;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(this.masterGain!);

    osc.type = 'square';
    osc.frequency.value = 1000;

    gain.gain.setValueAtTime(0.1, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + 0.05);

    osc.start(now);
    osc.stop(now + 0.05);
  }

  /**
   * Play countdown beep
   */
  playCountdown(final: boolean = false): void {
    if (!this.canPlay()) return;

    const ctx = this.ctx!;
    const now = ctx.currentTime;

    const osc = ctx.createOscillator();
    const gain = ctx.createGain();

    osc.connect(gain);
    gain.connect(this.masterGain!);

    osc.type = 'sine';
    osc.frequency.value = final ? 880 : 440;

    const duration = final ? 0.3 : 0.15;
    gain.gain.setValueAtTime(0.2, now);
    gain.gain.exponentialRampToValueAtTime(0.01, now + duration);

    osc.start(now);
    osc.stop(now + duration);
  }

  /**
   * Check if audio can be played
   */
  private canPlay(): boolean {
    if (!this.enabled || !this.ctx || !this.masterGain) {
      return false;
    }

    // Auto-init on first play attempt
    if (!this.initialized) {
      this.init();
    }

    this.resume();
    return true;
  }
}
